import React from 'react'

import { useNavigate } from 'react-router'

import { Schedule } from '@/core/api/entities/schedule'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ScheduleCard } from '@/features/schedule/components/ScheduleCard'
import { AddButton } from '@/features/zones/components/AddButton'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
} from '@/shared/components/molecules/Carousel'

interface SchedulesSectionProps {
	schedules: Schedule[]
	onAdd?: () => void
	onRemove?: (scheduleId: number) => void
	isLoading?: boolean
}

export const SchedulesSection: React.FC<SchedulesSectionProps> = ({
	schedules,
	onAdd,
	onRemove,
	isLoading = false,
}) => {
	const { t } = useTranslation('zones')
	const navigate = useNavigate()

	const handleScheduleClick = (schedule: Schedule) => {
		navigate(`/schedules/${schedule.id}`)
	}

	if (isLoading) {
		return (
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">
						{t('details.schedules.title')}
					</h2>
				</div>
				<div className="flex gap-4">
					{[1, 2, 3].map((i) => (
						<div key={i} className="w-[200px] space-y-3">
							<div className="aspect-square rounded-lg bg-muted animate-pulse" />
							<div className="h-4 w-32 bg-muted rounded animate-pulse" />
						</div>
					))}
				</div>
			</div>
		)
	}

	if (schedules.length === 0) {
		return (
			<div className="space-y-4 mb-12">
				<div className="flex items-center gap-3">
					<h2 className="text-xl font-semibold">
						{t('details.schedules.title')}
					</h2>
					{onAdd && <AddButton onAdd={onAdd} />}
				</div>
				<div className="text-sm text-muted-foreground">
					{t('details.schedules.noSchedules')}
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-4 mb-12">
			<div className="flex items-center gap-3">
				<h2 className="text-xl font-semibold">
					{t('details.schedules.title')}
				</h2>
				{onAdd && <AddButton onAdd={onAdd} />}
			</div>

			<Carousel
				opts={{
					align: 'start',
					slidesToScroll: 1,
				}}
				className="w-full"
			>
				<CarouselContent className="-ml-4 flex flex-wrap">
					{schedules.map((schedule) => (
						<CarouselItem key={schedule.id} className="pl-4 basis-auto pb-4">
							<ScheduleCard
								schedule={schedule}
								onClick={handleScheduleClick}
								onRemove={onRemove ? (id) => onRemove(id) : undefined}
								removeVariant="icon"
								className="w-[200px]"
							/>
						</CarouselItem>
					))}
				</CarouselContent>
			</Carousel>
		</div>
	)
}
